/**
 * The rows of the Accounts list a setup drew, each with its type, and the
 * owner's home money out of profit (L15, L52 of docs/plans/ledger.md).
 *
 *   a row's type     every row of the Accounts list names its type, or Build
 *                    cannot make its account (30 Oct, a gym: "Kiro, Tokens,
 *                    Wifi" seeded with no type, and Build failed four times).
 *                    A row whose type is clear gets it (`accountRowTypeOf`):
 *                    the type a rule's role that names it needs, a mode's
 *                    (Cash, a card, an overdraft), home money's, else the one
 *                    the lists' category picks add (an expense where only
 *                    Money out picks a category). A row that repeats a typed
 *                    row's name goes. One whose type is not clear stays
 *                    untyped, and the validator says so (ACCOUNTS_ROW_TYPE).
 *   home money       a row the owner named as home money (For home, ghar,
 *                    school fees, ration) is the owner's drawings, never an
 *                    expense of the business (30 Oct, a distributor: Ration
 *                    and School fees typed Expense beside For home cut the
 *                    profit). So is every row a home spending record (Ghar
 *                    kharcha) names. Each folds into For home: the row goes,
 *                    a rule that named it names drawings, and For home joins
 *                    the list when it lacks one (`homeMoneyOps`).
 *
 *   owner's money    money the owner brings in from their own pocket is
 *                    owner's money (capital), never income: a record whose
 *                    name says so (Owner's money in, Capital, Apna paisa)
 *                    posts it to Owner's money put in, and that row joins
 *                    the list when it lacks one (`ownerMoneyInOps`). 30 Oct,
 *                    a grocery chain: Owner's money in posted to Sales.
 *
 * Pure: the studio's reducer types the rows on every batch that reaches the
 * list, and the money pass and Build do the rest.
 */
import type { SetupDraftSeedRow } from '../compose-author';
import { isPostingPattern, type PostingPattern } from '../ledger/patterns';
import type { StudioDraft, StudioRecord } from './draft';
import { accountRowName, accountSeedRow, accountsListOf, modeAccountOf, postingOf, unheldAccountOf, type MoneyAccountType, type MoneyRoleSource, type MoneyRule } from './money-rules';
import type { StudioOp } from './ops';

const TYPES: ReadonlySet<string> = new Set(['asset', 'liability', 'equity', 'income', 'expense']);

/** Words that say money went to the owner's home (L52): For home, ghar kharcha, school fees, ration, ambil pribadi, peribadi and yuran sekolah in Malay. */
const HOME_MONEY = /\b(for home|home|ghar\w*|household|house|personal|pribadi|peribadi|rumah tangga|dapur rumah|belanja rumah|duit rumah|family|ration|school ?fees?|tuition fees?|yuran sekolah|kids'? fees?|bachch?on)\b/i;
/** Words with a home word in them that are the business's own (home delivery, a personal trainer, housekeeping, a guest house). */
const NOT_HOME = /\b(home ?deliver\w*|personal ?train\w*|house ?keep\w*|guest ?house|ware ?house|in-?house)\b/i;

/** Whether an account's or a record's name says it is the owner's home money. */
export const saysHomeMoney = (name: string): boolean => HOME_MONEY.test(name) && !NOT_HOME.test(name);

/** The type a row's Type value names (an option's key or label), or null when it names none. */
const typeOfValue = (list: StudioRecord, row: SetupDraftSeedRow): MoneyAccountType | null => {
  const typeKey = list.accounts?.typeFieldKey;
  const said = row.values.find((one) => one.fieldKey === typeKey)?.value;
  if (said === undefined || String(said).trim() === '') return null;
  const word = String(said).trim().toLowerCase();
  const options = list.fields.find((field) => field.key === typeKey)?.options ?? [];
  const option = options.find((one) => one.key.toLowerCase() === word || one.label.trim().toLowerCase() === word);
  const key = option?.key ?? word;
  return TYPES.has(key) ? (key as MoneyAccountType) : null;
};

/** Every role source of a rule, with the pattern and role it serves: its roles, its choices', a choice's options and a lines role's `each`. */
const roleSourcesOf = (rule: MoneyRule): Array<{ pattern: PostingPattern; role: string; source: MoneyRoleSource }> => {
  const out: Array<{ pattern: PostingPattern; role: string; source: MoneyRoleSource }> = [];
  const walk = (pattern: PostingPattern, role: string, source: MoneyRoleSource): void => {
    out.push({ pattern, role, source });
    if (!('fieldKey' in source)) return;
    for (const one of Object.values(source.options ?? {})) walk(pattern, role, one);
    for (const [sub, one] of Object.entries(source.each ?? {})) walk(pattern, `${role}.${sub}`, one);
  };
  const ways: Array<[string, MoneyRule['roles']]> = [[rule.pattern, rule.roles], ...Object.values(rule.byChoice?.options ?? {}).map((one): [string, MoneyRule['roles']] => [one.pattern ?? rule.pattern, one.roles])];
  for (const [pattern, roles] of ways) {
    if (!isPostingPattern(pattern)) continue;
    for (const [role, source] of Object.entries(roles ?? {})) walk(pattern, role, source);
  }
  return out;
};

/** The pattern and role of every `{ row }` that names this account, across the draft's rules. */
const rolesNaming = (draft: Pick<StudioDraft, 'records'>, name: string): Array<{ record: string; pattern: PostingPattern; role: string }> => {
  const word = name.trim().toLowerCase();
  return draft.records.flatMap((record) => {
    const rule = postingOf(record);
    if (rule === null) return [];
    return roleSourcesOf(rule)
      .filter((one) => 'row' in one.source && one.source.row.trim().toLowerCase() === word)
      .map((one) => ({ record: record.key, pattern: one.pattern, role: one.role }));
  });
};

/** The types a category pick of the list adds a typed row as: an expense on Money out, an income on Money in (L16). */
const addedTypesOf = (draft: Pick<StudioDraft, 'records'>, list: StudioRecord): Set<'income' | 'expense'> =>
  new Set(
    draft.records.flatMap((record) =>
      record.fields.flatMap((field) => (field.type === 'reference' && field.targetRecordKey === list.key && field.accounts?.addAs !== undefined ? [field.accounts.addAs] : [])),
    ),
  );

/**
 * The type a row of the Accounts list that says none takes, when it is
 * clear; null when it is not. The roles of the rules that name it first (an
 * expense role an expense, a place money is in money you have), then its
 * name as a mode (Cash, UPI, an overdraft), then home money (owner's money:
 * `homeMoneyOps` folds it into For home), then the one type the list's
 * category picks add.
 */
export const accountRowTypeOf = (draft: Pick<StudioDraft, 'records'>, list: StudioRecord, name: string): MoneyAccountType | null => {
  const needed = new Set(rolesNaming(draft, name).flatMap((one) => unheldAccountOf(one.pattern, one.role, name).row?.type ?? []));
  if (needed.size === 1) return [...needed][0]!;
  if (needed.size > 1) return null;
  const mode = modeAccountOf(name, 'in');
  if (mode.row?.type !== null && mode.row?.type !== undefined) return mode.row.type;
  if ('systemKey' in mode.source && (mode.source.systemKey === 'cash' || mode.source.systemKey === 'bank')) return 'asset';
  if (saysHomeMoney(name)) return 'equity';
  const added = addedTypesOf(draft, list);
  return added.size === 1 ? [...added][0]! : null;
};

/**
 * The Accounts list's rows, each with its type where it was missing and is
 * clear, and a row that only repeats a typed row's name gone; null when no
 * row changes. A row whose type is not clear is kept as it is.
 */
export const typedAccountRows = (draft: Pick<StudioDraft, 'records'>): SetupDraftSeedRow[] | null => {
  const list = accountsListOf(draft);
  const typeKey = list?.accounts?.typeFieldKey;
  if (list === null || typeKey === undefined || (list.seedRows ?? []).length === 0) return null;
  const rows = list.seedRows ?? [];
  const typedNames = new Set(rows.filter((row) => typeOfValue(list, row) !== null).flatMap((row) => accountRowName(list, row)?.toLowerCase() ?? []));
  const seen = new Set<string>();
  let changed = false;
  const out: SetupDraftSeedRow[] = [];
  for (const row of rows) {
    const name = accountRowName(list, row);
    if (typeOfValue(list, row) !== null || name === null || name === '' || row.account?.systemKey !== undefined) {
      out.push(row);
      continue;
    }
    const word = name.toLowerCase();
    // "Cash" seeded twice, once with its type: the typed row is the account.
    if (typedNames.has(word) || seen.has(word)) {
      changed = true;
      continue;
    }
    seen.add(word);
    const type = accountRowTypeOf(draft, list, name);
    if (type === null) {
      out.push(row);
      continue;
    }
    changed = true;
    out.push({ ...row, values: [...row.values.filter((one) => one.fieldKey !== typeKey), { fieldKey: typeKey, value: type }] });
  }
  return changed ? out : null;
};

/** The op that types the Accounts list's rows where their type is clear (`typedAccountRows`), or none. */
export const accountTypeOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  const rows = typedAccountRows(draft);
  return list === null || rows === null ? [] : [{ op: 'seed_list', record: list.key, rows, mode: 'replace' }];
};

/** Whether a record keeps the owner's home spending: money out whose name says home (Ghar kharcha, Home expenses). */
const isHomeRecord = (record: StudioRecord): boolean => record.kind === 'record' && record.direction === 'out' && saysHomeMoney(record.name);

/** The roles that say what the money was for: on money out, the business's cost, unless it is home money. */
const FOR_ROLES: ReadonlySet<string> = new Set(['account', 'expense']);

/**
 * The ops that fold the owner's home money into For home (L52): every row
 * of the Accounts list the owner named as home money, typed an expense,
 * owner's money with no key of its own, or untyped, goes, and every rule that
 * named it names drawings instead. A home spending record's rule (Ghar
 * kharcha) names drawings for whatever it was for, and the rows it named stay
 * for the business to pick. For home joins the list when it holds none. A row
 * a money-in rule names (fees a school charges) is never home money. Empty
 * when nothing is home money.
 */
export const homeMoneyOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const rows = list.seedRows ?? [];
  const homeRecords = new Set(draft.records.filter(isHomeRecord).map((record) => record.key));
  const home = new Set<string>();
  for (const row of rows) {
    const name = accountRowName(list, row);
    if (name === null || name === '' || row.account?.systemKey !== undefined || !saysHomeMoney(name)) continue;
    const type = typeOfValue(list, row);
    if (type !== null && type !== 'expense' && type !== 'equity') continue;
    // Money in never names home money: a school's fees are its income.
    if (rolesNaming(draft, name).some((one) => !FOR_ROLES.has(one.role) || one.pattern === 'money_in' || one.pattern === 'sale' || one.pattern === 'receipt')) continue;
    home.add(name.toLowerCase());
  }
  /** Whether a home spending record still names a row for what it was for. */
  const homeNamesRow = draft.records.some((record) => homeRecords.has(record.key) && roleSourcesOf(postingOf(record) ?? { pattern: 'money_out' }).some((one) => FOR_ROLES.has(one.role) && 'row' in one.source));
  if (home.size === 0 && !homeNamesRow) return [];
  const ops: StudioOp[] = [];
  const kept = rows.filter((row) => !home.has(accountRowName(list, row)?.toLowerCase() ?? ''));
  const holdsDrawings = kept.some((row) => row.account?.systemKey === 'drawings');
  if (home.size > 0 || !holdsDrawings) {
    ops.push({ op: 'seed_list', record: list.key, rows: holdsDrawings ? kept : [...kept, accountSeedRow(list, { name: 'For home', type: 'equity' }, 'drawings')], mode: 'replace' });
  }
  // A rule that named a home row names drawings, and so does a home spending record for what it was for.
  const drawings: MoneyRoleSource = { systemKey: 'drawings' };
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null) continue;
    const ownHome = homeRecords.has(record.key);
    let changed = false;
    const mend = (role: string, source: MoneyRoleSource): MoneyRoleSource => {
      if ('row' in source) {
        if (!home.has(source.row.trim().toLowerCase()) && !(ownHome && FOR_ROLES.has(role))) return source;
        changed = true;
        return drawings;
      }
      if (!('fieldKey' in source)) return source;
      const options = source.options === undefined ? undefined : Object.fromEntries(Object.entries(source.options).map(([key, one]) => [key, mend(role, one)]));
      const each = source.each === undefined ? undefined : Object.fromEntries(Object.entries(source.each).map(([key, one]) => [key, mend(`${role}.${key}`, one)]));
      return { ...source, ...(options === undefined ? {} : { options }), ...(each === undefined ? {} : { each }) };
    };
    const rolesOf = (roles: MoneyRule['roles']): MoneyRule['roles'] => (roles === undefined ? roles : Object.fromEntries(Object.entries(roles).map(([role, one]) => [role, mend(role, one)])));
    const roles = rolesOf(rule.roles);
    const byChoice =
      rule.byChoice === undefined
        ? undefined
        : { ...rule.byChoice, options: Object.fromEntries(Object.entries(rule.byChoice.options).map(([key, one]) => [key, one.roles === undefined ? one : { ...one, roles: rolesOf(one.roles)! }])) };
    if (changed) ops.push({ op: 'update_record', record: record.key, posting: { ...rule, ...(roles === undefined ? {} : { roles }), ...(byChoice === undefined ? {} : { byChoice }) } });
  }
  return ops;
};

/** Words that name money the owner put in from their own pocket: owner's money in, capital, apna paisa, modal, wang sendiri. */
const OWNER_MONEY_IN =
  /\b(capital|modal|owner'?s?\s+(own\s+)?(money|cash|funds?|investment|contribution|pocket)|own\s+(money|funds?|cash|pocket)|(my|owner'?s?)\s+pocket|pocket\s+money|apna\s+paisa|apne\s+paise|malik\s+(ka|ke)\s+pais[ae]|jeb\s+se|(wang|duit|uang)\s+sendiri|put\s+in\s+by\s+(the\s+)?owner)\b/i;

/** Whether a record's name says it keeps money the owner put in (Owner's money in, Capital put in). */
export const saysOwnerMoneyIn = (name: string): boolean => OWNER_MONEY_IN.test(name);

/** Whether a role names income: the sales key, or a row the list holds as income, or a choice of them. */
const namesIncome = (list: StudioRecord, source: MoneyRoleSource): boolean => {
  if ('systemKey' in source) return source.systemKey === 'sales' || source.systemKey === 'other_income';
  if ('row' in source) {
    const row = (list.seedRows ?? []).find((one) => accountRowName(list, one)?.toLowerCase() === source.row.trim().toLowerCase());
    return row === undefined || typeOfValue(list, row) === 'income';
  }
  return 'fieldKey' in source && Object.values(source.options ?? {}).some((one) => namesIncome(list, one));
};

/**
 * The ops that post money the owner put in to Owner's money put in (equity),
 * never income: a money-in record whose name says it is the owner's own
 * money, with a rule that counts it as a sale or income, becomes a money in
 * to the owner's capital, its place kept. The list gets the row when it holds
 * none. A rule that names something else (a loan, a transfer) is left. Empty
 * when no such record counts income.
 */
export const ownerMoneyInOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const capital: MoneyRoleSource = { systemKey: 'owner_capital' };
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (record.kind !== 'record' || rule === null || rule.byChoice !== undefined || !saysOwnerMoneyIn(record.name)) continue;
    const roles = rule.roles ?? {};
    const income = rule.pattern === 'money_in' ? roles.account : rule.pattern === 'sale' ? roles.income : undefined;
    if (income === undefined || !namesIncome(list, income)) continue;
    const place = roles.paid_to;
    const amount = roles.amount;
    if (place === undefined || amount === undefined) continue;
    ops.push({ op: 'update_record', record: record.key, posting: { pattern: 'money_in', roles: { amount, account: capital, paid_to: place } } });
  }
  if (ops.length > 0 && !(list.seedRows ?? []).some((row) => row.account?.systemKey === 'owner_capital')) {
    ops.unshift({ op: 'seed_list', record: list.key, rows: [accountSeedRow(list, { name: "Owner's money put in", type: 'equity' }, 'owner_capital')], mode: 'append' });
  }
  return ops;
};
